import { ChangeEvent, useEffect, useState } from "react";
import { dateFormat } from "../../../../services/Utils/functionService";
import {
  getCustomersRequest,
  patchRequest,
} from "../../../../services/Api/requestService";
import { usePartnerRequestDetailsStore } from "../../../../stores/Cac/Partner/partnerRequestDetails";
import { usePartnerRequestsStore2 } from "../../../../stores/Cac/Partner/partnerRequests";
import {
  showToastError,
  showToastSuccess,
} from "../../../../services/Toasts/toasts";
import { getUsersCustomerToMission } from "../../../../services/Api/customerService";
import { useParams } from "react-router-dom";

const RequestDetails = () => {
  // Store
  const {
    request,
    comment,
    setComment,
    clientsAssign,
    setClientsAssign,
    clientsAssignToMission,
    setClientsAssignToMission,
    openCommentary,
    setOpenCommentary,
    checkboxValues,
    setCheckboxValues,
    limitDate,
    setLimitDate,
  } = usePartnerRequestDetailsStore();
  const { requestId, updateRequest, setUpdateRequest, missionData } =
    usePartnerRequestsStore2();
  const { idCustomer, idMission } = useParams();
  // State
  const [compteur, setCompteur] = useState<number>(255);

  useEffect(() => {
    if (requestId !== 0) {
      getCustomersRequest(requestId)
        .then((data) => {
          setClientsAssign(data);
          getUsersCustomerToMission(Number(idCustomer), idMission)
            .then((users) => {
              setClientsAssignToMission(users);

              if (users.length === 0) {
                return;
              }
              const clientCheckboxValues: { [key: number]: boolean } = {};
              users?.forEach((user: any) => {
                clientCheckboxValues[user.id] = data.some(
                  (client: any) => client.id === user.id,
                );
              });
              setCheckboxValues(clientCheckboxValues);
              setComment(request?.Commentaries?.[0]?.text || "");
            })
            .catch(() => showToastError("Une erreur s'est produite"));
        })
        .catch(() => showToastError("Une erreur s'est produite"));
    }
  }, [requestId, idCustomer, idMission, updateRequest]);

  // This is for block commentary input when compteur is 0
  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nouveauTexte = e.target.value;
    const nouveauCompteur = 255 - nouveauTexte.length;
    setComment(nouveauTexte);
    setCompteur(nouveauCompteur);
  };

  const handleButtonClick = async () => {
    const dataUpdate = {
      dueDate: limitDate,
      idUsers: Object.entries(checkboxValues)
        .filter(([_, value]) => value)
        .map(([key]) => Number(key)),
      description: comment,
    };

    patchRequest(requestId, dataUpdate)
      .then(() => {
        setOpenCommentary(false);
        showToastSuccess("Les modifications ont bien été prises en compte");
        setUpdateRequest(!updateRequest);
        setComment("");
      })
      .catch(() => {
        showToastError("Une erreur est survenue lors de la modification");
      });
  };

  const handleCheckboxChange = (value: number, checked: boolean) => {
    setCheckboxValues({ ...checkboxValues, [value]: checked });
  };

  useEffect(() => {
    if (openCommentary) {
      setComment(request?.description || "");
    }
  }, [openCommentary]);

  return (
    <div className="mx-auto h-full w-full max-w-6xl space-y-2 overflow-y-auto pb-3">
      {/* Header Card */}
      <div className="bg-white/70 backdrop-blur-md rounded-xl p-3 border border-white/20 shadow-xl">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold text-gray-700 sm:text-xl">
            Détails de la demande
          </h2>
          <div className="flex items-center space-x-3">
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                request?.State?.state === "En cours"
                  ? "bg-yellow-500/20 text-yellow-300"
                  : request?.State?.state === "Terminé"
                    ? "bg-green-500/20 text-green-300"
                    : "bg-gray-500/20 text-gray-700"
              }`}
            >
              {request?.State?.state}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          <div className="space-y-2">
            <label className="text-sm font-light text-gray-700 uppercase tracking-wide">
              Catégorie
            </label>
            <p className="text-gray-700 font-medium">
              {request?.CategoryDocRequest?.name || "Non définie"}
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-light text-gray-700 uppercase tracking-wide">
              Demandeur
            </label>
            <p className="text-gray-700 font-medium">
              {request?.User?.fullName || "Non défini"}
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-light text-gray-700 uppercase tracking-wide">
              Date création
            </label>
            <p className="text-gray-700 font-medium">
              {dateFormat(request?.createdAt)}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white/70 backdrop-blur-sm rounded-xl p-3 border border-white/20 shadow-xl ">
        {/* Date Limite*/}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <label className="text-sm font-light text-gray-700 uppercase tracking-wide">
            Date limite
          </label>
          {!openCommentary ? (
            <p className="text-gray-700 font-medium text-lg">
              {dateFormat(request?.dueDate)}
            </p>
          ) : (
            <div className="mt-2">
              <input
                type="date"
                value={limitDate ? limitDate.slice(0, 10) : ""}
                onChange={(e: any) => setLimitDate(e.target.value)}
                className="w-full rounded-lg h-10 p-2 text-gray-700 bg-white/70 border border-white/20 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200"
              />
            </div>
          )}
        </div>

        {/* Description*/}
        <label className="text-sm font-light text-gray-700 uppercase tracking-wide mt-2 mb-2 block">
          Description
        </label>
        {!openCommentary ? (
          <div className="min-h-24 rounded-lg bg-white/70 p-3 sm:min-h-[120px] sm:p-4">
            <p className="text-gray-700 whitespace-pre-line leading-relaxed">
              {request?.description || "Aucune description fournie"}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            <textarea
              name="comment"
              onChange={(e: any) => handleChange(e)}
              id="comment"
              disabled={compteur === 0}
              className="w-full rounded-lg h-32 p-4 text-gray-700 bg-white/70 border border-white/20 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200 resize-none"
              value={comment}
              placeholder="Entrez votre description..."
            />
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-400">
                {compteur} caractères restants
              </span>
            </div>
          </div>
        )}

        {/* Clients assignés*/}
        <label className="text-sm font-light text-gray-700 uppercase tracking-wide mb-2 block mt-2">
          Clients assignés
        </label>
        <div className="max-h-80 overflow-y-auto rounded-lg bg-white/70 p-2 sm:p-4">
          {openCommentary ? (
            <div className="space-y-3">
              {clientsAssignToMission.length > 0 ? (
                clientsAssignToMission?.map((user) => (
                  <div
                    className="flex items-center p-3 bg-white/70 rounded-lg "
                    key={user?.id}
                  >
                    <input
                      type="checkbox"
                      checked={checkboxValues[user?.id] || false}
                      onChange={() => {
                        handleCheckboxChange(
                          user?.id,
                          !checkboxValues[user?.id],
                        );
                      }}
                      className="w-4 h-4 text-blue-600 bg-white/10 border-white/30 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <div className="ml-4 flex-1">
                      {user.fullName !== "" ? (
                        <div className="space-y-1">
                          <p className="text-gray-700 font-medium">
                            {user.fullName}
                          </p>
                          <p className="text-gray-400 text-sm">{user.eMail}</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <p className="text-gray-700 font-medium">
                            {user.eMail}
                          </p>
                          <p className="text-gray-400 text-sm italic">
                            Compte pas encore créé
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 italic text-center py-8">
                  Aucun utilisateur disponible
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {clientsAssign?.map((user: any) => (
                <div
                  key={user?.id}
                  className="flex items-center p-3 bg-white/5 rounded-lg"
                >
                  <div className="flex-1">
                    {user.fullName !== "" ? (
                      <div className="space-y-1">
                        <p className="text-gray-400 font-medium">
                          {user.fullName}
                        </p>
                        <p className="text-gray-400 text-sm">{user.eMail}</p>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        <p className="text-gray-400 font-medium">
                          {user.eMail}
                        </p>
                        <p className="text-gray-400 text-sm italic">
                          Compte pas encore créé
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Action Button */}
      {openCommentary && (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            className="min-h-11 w-full rounded-xl bg-gradient-to-r from-red-600 to-red-700 px-6 py-3 font-medium text-white shadow-lg transition-all duration-200 sm:mr-4 sm:w-auto sm:px-8 sm:hover:scale-105"
            type="button"
            onClick={() => {
              setOpenCommentary(false);
              setComment("");
              setCheckboxValues({});
            }}
            disabled={missionData?.closed === 1}
          >
            Annuler
          </button>
          <button
            className="min-h-11 w-full rounded-xl bg-gradient-to-r from-green-600 to-green-700 px-6 py-3 font-medium text-white shadow-lg transition-all duration-200 sm:w-auto sm:px-8 sm:hover:scale-105"
            type="button"
            disabled={missionData?.closed === 1}
            onClick={handleButtonClick}
          >
            Valider les modifications
          </button>
        </div>
      )}
    </div>
  );
};

export default RequestDetails;
